import { SelectionModel } from '@angular/cdk/collections';
import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource, MatTable } from '@angular/material/table';
import { ClientPlatformService } from 'src/app/client-platform/client-platform.service';
import { SpinnerService } from 'src/app/shared/spinner/spinner.service';
import { MappingIcons } from 'src/app/shared/mapping-setup/mapping-icons';
import { TokenUtil } from 'src/app/core/services/TokenUtil.service';

export interface TableElement {
  type: string;
  zoho_attr_name: string;
  app_attr_name: string;
  isEditingApp?: boolean;
  tempAppName?: string;
}

@Component({
    selector: 'app-create-object.dialog',
    templateUrl: './create-object.dialog.html',
    styleUrls: ['./create-object.dialog.scss'],
    standalone: false
})
export class CreateObjectDialogComponent implements OnInit {
  displayedColumns: string[] = ['select', 'type', 'zoho_attr_name', 'app_attr_name'];
  dataSource: any;
  selection = new SelectionModel<any>(true, []);
  isError: boolean = false;
  canContinueEnabled: boolean = false;
  statusMessage: string;
  objResponse: any;
  objectName: string;
  displayNameObj: any;
  MappingIcons = MappingIcons;  // Make it available in the template
  isLoading = false;
  
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public spinnerService: SpinnerService,
    public clientPlatformService: ClientPlatformService
  ) {}

  ngOnInit(): void {
    console.log('MAT_DIALOG_DATA:', this.data);

    this.objectName = this.data.sourceObjName?.toLowerCase();
    this.displayNameObj = this.data.displayNameObj || {};
    /*format attributes*/
    this.formatAttributes();
    this.selection.select(...this.dataSource.data);
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

   /** Selects all rows if they are not all selected; otherwise clear selection. */
  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...this.dataSource.data);
  }

  /** The label for the checkbox on the passed row */
  checkboxLabel(row?: any): string {
    if (!row) { 
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.position + 1}`;
  }

  onChangeOfObjectName(value: string) {
    this.objectName = value.toLowerCase().replaceAll(' ', '_');
  }

  formatAttributes() {
    const formattedAttr = this.data.attributes.map((attr) => ({ 
        type: attr.dataType,
        zoho_attr_name: attr.name,
        app_attr_name: attr.name.replaceAll(/[\/ ]/g, '_'),
        primary: attr.primary
    }))

    // Create a Set to track unique app_attr_name values
    const seenAppAttr = new Set();

    // Filter out attributes of type 'object' or 'array', and remove duplicates by app_attr_name
    const filteredAttr = formattedAttr.filter(attr => {
      // Skip if type is 'object' or 'array'
      if (attr.type === 'object' || attr.type === 'array') return false;
      // If we've already seen this app_attr_name, skip it
      if (seenAppAttr.has(attr.app_attr_name)) return false;
      // Otherwise, mark this app_attr_name as seen and keep the attribute
      seenAppAttr.add(attr.app_attr_name);
      return true;
    });

    // Replace formattedAttr with the filtered attributes
    formattedAttr.length = 0;
    formattedAttr.push(...filteredAttr);

    this.dataSource = new MatTableDataSource<any>(formattedAttr);
  }


  async createObject() {
    if (!this.objectName) {
      this.clientPlatformService.openErrorSnackBar("Please provide a name for the object");
      return;
    }

    this.isLoading = true;
    this.spinnerService.show();
    try {
      const selectedRows = this.selection.selected;
      if (selectedRows.length === 0) {
        this.clientPlatformService.openErrorSnackBar("Please select at least one field");
        return;
      }

      const attributes = selectedRows.map(row => ({
        name: row.app_attr_name,
        type: row.type,
        required: (row.required == undefined ? false : row.required)
      }));

      const payload = {
        // name: this.objectName,
        object: this.objectName,
        attributes: attributes
      };

      const response = await this.clientPlatformService.executeBoxFn(
        this.data.connection,
        { parameters: payload },
        'createobject'
      );

      if (response?.error) {
        this.isError = true;
        this.statusMessage = response.error.message || 'Failed to create object';
      } else {
        this.canContinueEnabled = true;
        this.objResponse = response?.result;
        this.statusMessage = `${
          this.displayNameObj?.app?.objDisplayName
            ? this.titleCase(this.displayNameObj.app.objDisplayName)
            : 'Object'
        } created successfully!`;
      }
    } catch (error) {
      console.error('Error creating object:', error);
      this.isError = true;
      this.statusMessage = error.message || 'Failed to create object';
    } finally {
      this.isLoading = false;
      this.spinnerService.hide();
    }
  }

  createAnother() {
    this.objectName = "";
    this.canContinueEnabled = false;
    this.statusMessage = "";
  }

  titleCase(str: string) {
    return str.toLowerCase().replace(/\b\w/g, (s) => s.toUpperCase());
  }

  showEditIcon(element: TableElement): void {
    // This is handled by CSS hover
  }

  hideEditIcon(element: TableElement): void {
    // This is handled by CSS hover
  }

  startEditing(element: TableElement): void {
    element.isEditingApp = true;
    element.tempAppName = element.app_attr_name;
    // Focus the input after view updates
    setTimeout(() => {
      const input = document.querySelector(`#app-${element.app_attr_name}`) as HTMLInputElement;
      if (input) input.focus();
    });
  }

  onNameInput(event: Event, element: TableElement): void {
    const value = (event.target as HTMLInputElement).value;
    element.tempAppName = value;
  }

  confirmEdit(element: TableElement): void {
    if (element.tempAppName?.trim()) {
      element.app_attr_name = element.tempAppName.trim();
    }
    element.isEditingApp = false;
  }
}
